import { useMemo, useState } from 'react'

import './_singleSetting.scss'
import { useTranslation } from 'react-i18next'
import { IoCopyOutline } from "react-icons/io5";
import { LuRefreshCcw } from "react-icons/lu";
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import * as spriteIcons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { AllIcons } from 'helpers/allIcons'
import { putLiveStreamPrice } from 'services/endpoints/settings/account/profileSettings'
import {
  useObsSettings,
  useRegenerateObsKey,
} from 'services/endpoints/streaming'

import { SuccessfulChange } from '../SuccessfulChange'

type ObsSettingCardProps = {
  label: string
  value: string
  onCopy: () => void
  onRegenerate?: () => void
  regenerateLoading?: boolean
}

const ObsSettingCard = ({
  label,
  value,
  onCopy,
  onRegenerate,
  regenerateLoading = false,
}: ObsSettingCardProps) => {

  const RefreshIcon = LuRefreshCcw as React.ComponentType<{ color?: string; style?: React.CSSProperties }>
const CopyIcon = IoCopyOutline as React.ComponentType<{ color?: string; style?: React.CSSProperties }>
  return (
    <div
      className='obsSettingCard'
      onClick={onCopy}
      role='button'
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onCopy()
        }
      }}
    >
      <div className='obsSettingCard__icon'>
        <img src={AllIcons.dollar_with_arrows} alt='' />
      </div>

      <div className='obsSettingCard__body'>
        <div className='obsSettingCard__label'>{label}</div>
        <div className='obsSettingCard__value' title={value}>
          {value || '—'}
        </div>
      </div>

      <div className='obsSettingCard__actions'>
        {onRegenerate && (
          <button
            type='button'
            className='obsSettingCard__actionBtn'
            disabled={regenerateLoading}
            onClick={e => {
              e.stopPropagation()
              onRegenerate()
            }}
          >
            <RefreshIcon color='#333' />
          </button>
        )}

        <button
          type='button'
          className='obsSettingCard__actionBtn'
          onClick={e => {
            e.stopPropagation()
            onCopy()
          }}
        >
          <CopyIcon color='#333' />
        </button>
      </div>
    </div>
  )
}

export const LiveStream = ({ page }: { page: string }) => {
  const [price, setPrice] = useState<number>(3)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { data: obsSettings } = useObsSettings()
  const regenerateMutation = useRegenerateObsKey()

  const submitFunction = useMutation(
    async () => await putLiveStreamPrice({ live_stream_price: price }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('user-profile')
        setCurrentPage(2)
      },
      onError: err => {
        console.error(err)
        addToast('error', t('error:errorSomethingWentWrong'))
      },
    }
  )

  const obsFields = useMemo(
    () => [
      {
        key: 'obs_server',
        label: t('obsServer'),
        value: obsSettings?.obs_server ?? '',
      },
      {
        key: 'obs_key',
        label: t('obsKey'),
        value: obsSettings?.obs_key ?? '',
        regenerateType: 'primary' as const,
      },
    ],
    [obsSettings, t]
  )

  const handleCopy = async (value: string) => {
    if (!value) {
      addToast('error', t('error:errorSomethingWentWrong'))
      return
    }

    try {
      await navigator.clipboard.writeText(value)
    } catch (err) {
      console.error(err)
      addToast('error', t('error:errorSomethingWentWrong'))
    }
  }

  const handleRegenerate = (type: 'primary') => {
    regenerateMutation.mutate(
      { type },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['obs-settings'])
        },
        onError: err => {
          console.error(err)
          addToast('error', t('error:errorSomethingWentWrong'))
        },
      }
    )
  }

  if (page === 'cards') {
    return (
      <>
        <ActionCard
          icon={AllIcons.dollar_with_arrows}
          iconSize='small'
          text={t('setPrice')}
          description='Set a default entry price to view your streams'
          hasArrow
          link='/settings/general/live-stream/set-price'
        />

        <div className='singleSetting__obsSection'>
          <div className='singleSetting__obsList'>
            {obsFields.map(field => (
              <ObsSettingCard
                key={field.key}
                label={field.label}
                value={field.value}
                onCopy={async () => await handleCopy(field.value)}
                onRegenerate={
                  field.regenerateType
                    ? () => handleRegenerate(field.regenerateType)
                    : undefined
                }
                regenerateLoading={regenerateMutation.isLoading}
              />
            ))}
          </div>
        </div>
      </>
    )
  }

  if (currentPage === 1) {
    return (
      <>
        <InputCard
          hasIcon
          icon={AllIcons.dollar_with_arrows}
          type='number'
          label={t('pricePerMinute')}
          isTextArea={false}
          value={price}
          changeFn={(val: number) => {
            setPrice(val)
          }}
        />

        <Button
          text={t('save')}
          color='black'
          font='mont-16-bold'
          width='20'
          height='5'
          disabled={!price || price < 3}
          clickFn={() => {
            submitFunction.mutate()
          }}
          customClass='singleSetting__saveBtn'
        />
      </>
    )
  }

  return (
    <>
      <SuccessfulChange
        img={<spriteIcons.IconManCircleBlue />}
        text={t('successfullyUpdatedMiniBio')}
      />
      <Button
        text={t('close')}
        color='black'
        font='mont-16-bold'
        width='20'
        height='5'
        clickFn={() => {
          setCurrentPage(1)
          navigate('/settings/general/live-stream')
        }}
        customClass='singleSetting__saveBtn'
      />
    </>
  )
}